import { useMemo, useState } from 'react'
import { calcStroke, shuffleRotation, computeShuffleSegments, autoShuffle, manualShuffle } from './ggShuffle.js'
import { samplePlayers } from './ggSamplePlayers.js'

// G-Shuffle — isolated sandbox. Auto-settle (ON) computes each segment from the
// scores + the defined partner rotation and settles $ zero-sum. Manual (OFF) is
// the track-it-yourself fallback: enter who won each segment.
const cell = { border: '1px solid #ddd', padding: '6px 10px', fontSize: 16 }
const Seg = ({ options, value, onChange }) => (
  <div className="flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
    {options.map((o) => {
      const on = value === o.value
      return <button key={String(o.value)} onClick={() => onChange(o.value)} className="rounded-md px-3 py-1 text-xs font-bold uppercase tracking-wider transition"
        style={on ? { backgroundColor: '#1B4D2E', color: '#fff' } : { backgroundColor: 'transparent', color: '#374151' }}>{o.label}</button>
    })}
  </div>
)

export default function GShuffle() {
  const players = useMemo(() => samplePlayers().slice(0, 4), [])
  const rotation = useMemo(() => shuffleRotation(players.map((p) => p.id)), [players])
  const [auto, setAuto] = useState(true)
  const [dps, setDps] = useState(5)
  const [mWinners, setMWinners] = useState(['A', 'A', 'A'])
  const segsAuto = useMemo(() => computeShuffleSegments(players, rotation), [players, rotation])

  const result = useMemo(
    () => (auto ? autoShuffle(players, rotation, Number(dps) || 0) : manualShuffle(mWinners, rotation, Number(dps) || 0)),
    [auto, players, rotation, dps, mWinners],
  )
  const nm = (id) => players.find((p) => p.id === id)?.name.split(' ')[0] || id
  const pair = (t) => t.map(nm).join(' · ')

  return (
    <div className="card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Seg options={[{ value: true, label: 'Auto-settle' }, { value: false, label: 'Manual' }]} value={auto} onChange={setAuto} />
        <span className="text-sm text-gray-600 font-semibold">$/segment<input type="number" className="w-14 border rounded px-1 py-0.5 ml-1" value={dps} onChange={(e) => setDps(e.target.value)} /></span>
      </div>

      <table className="w-full text-base mb-4" style={{ borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#f6f6f2', color: '#555' }}>{['Holes', 'Team A', 'Team B', 'Winner'].map((h) => <th key={h} style={{ ...cell, textAlign: 'left' }}>{h}</th>)}</tr></thead>
        <tbody>{rotation.map((seg, i) => {
          const s = segsAuto[i]
          return (
            <tr key={i}>
              <td style={cell}>{seg.holes}</td>
              <td style={cell}>{pair(seg.teams[0])}{auto ? <span className="text-gray-400"> ({s.aTotal})</span> : null}</td>
              <td style={cell}>{pair(seg.teams[1])}{auto ? <span className="text-gray-400"> ({s.bTotal})</span> : null}</td>
              <td style={cell}>
                {auto ? <b>{s.winner === 'tie' ? 'Tie' : `Team ${s.winner}`}</b>
                  : <select className="border rounded px-1 py-0.5 text-xs" value={mWinners[i]} onChange={(e) => setMWinners((w) => w.map((x, j) => (j === i ? e.target.value : x)))}><option value="A">Team A</option><option value="B">Team B</option><option value="tie">Tie</option></select>}
              </td>
            </tr>
          )
        })}</tbody>
      </table>

      <table className="w-full text-base" style={{ borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#f6f6f2', color: '#555' }}>{['Player', 'Settlement'].map((h) => <th key={h} style={{ ...cell, textAlign: 'left' }}>{h}</th>)}</tr></thead>
        <tbody>{players.map((p) => {
          const v = result.payouts[p.id] || 0
          return <tr key={p.id}><td style={cell}>{p.name}</td><td style={cell}><b style={{ color: v >= 0 ? '#1B7A3E' : '#B42318' }}>{v >= 0 ? '+' : '−'}${Math.abs(v)}</b></td></tr>
        })}</tbody>
      </table>
    </div>
  )
}
